import React, { useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"

interface TeamMember {
  name: string;
  location: string;
  image: string;
  specialties: string[];
  bio: string[];
}

interface TeamDetails {
  [key: string]: TeamMember;
}

// Team member details data
const teamDetails: TeamDetails = {
  vignesh: {
    name: "Vignesh Rama",
    location: "San Francisco, CA",
    image: "/assets/images/teams/vignesh.png",
    specialties: ["Enterprise Architecture", "AI/ML", "Virtualization", "Networking"],
    bio: [
      "I have two decades of experience building web-scale enterprise technologies for Fortune 500 companies. My journey has been focused on transforming complex technical challenges into scalable solutions that drive business growth.",
      "I help incubate, scale and take to market new horizon technologies in AI, Virtualization, and Networking. FOWND as a concept can truly transform the Physical Therapy industry, benefiting both the profession and patients.",
      "The amalgamation of SaaS technologies and gig economy concepts in healthcare are going to be the catalyst that FOWND brings to bear, to become the gold standard for all healthcare delivery.",
    ],
  },
  christina: {
    name: "Christina Rama, PT, DPT",
    location: "San Francisco, CA",
    image: "/assets/images/teams/christina.png",
    specialties: ["Physical Therapy", "Healthcare Innovation", "Patient Care", "Clinical Operations"],
    bio: [
      "I have worked in outpatient, inpatient, neuro rehab, home health, and SNFs. My experience in various states across the US gives me the unique perspective to disrupt the industry with innovation.",
      "Improving lives is at the core of physical therapy, we need to take better care of both therapists and patients. We intend to change how patients and therapists connect to improve overall therapist (provider) sentiment.",
      "With FOWND, I have set out to address a severely lacking technology, innovation, and professional satisfaction gap. Our Mission is to provide high-quality, flexible care that works for everyone in the healthcare ecosystem.",
    ],
  },
  sowmya: {
    name: "Sowmya Shetty",
    location: "Detroit, MI",
    image: "/assets/images/teams/sowmya.png",
    specialties: ["Healthcare Operations", "Entrepreneurship", "Team Leadership", "Strategic Planning"],
    bio: [
      "I am a dentist by education and an entrepreneur in the making. My background in healthcare has been pivotal in helping shape FOWND's vision to create a better healthcare system, and I am proud to be part of a team that is working to improve the healthcare system one step at a time.",
      "I believe in continuous learning and adaptability, and I wear the team's orchestrator hat, helping to turn our collective ideas into action. Together, we're not just working toward change—we're building a future where healthcare works better for everyone.",
    ],
  },
  rachel: {
    name: "Rachel Stewart",
    location: "North Carolina, USA",
    image: "/assets/images/teams/rachel.png",
    specialties: ["UX Design", "Physical Therapy", "Healthcare Technology", "User Experience"],
    bio: [
      "I began my journey in healthcare as a Doctor of Physical Therapy. For 6 years, I have worked in a variety of clinical settings only to discover frustration with high volumes of documentation coupled with complex EMR systems. This repetitive experience led me to discover the importance of user experience in the health-tech realm.",
      "As a UX Designer at Fownd, my hope is to improve how clinicians interact with their documentation system, thus allowing them to provide better patient care. Just as I advocated for my patients in the clinic, through Fownd's mission, I continue to advocate for innovative, accessible, and high-quality experiences for our users.",
    ],
  },
  // swagata: {
  //   name: "Swagatha Mandal",
  //   location: "Bengaluru, INDIA",
  //   image: "/assets/images/teams/Swagatha.png",
  //   specialties: ["Technology Strategy", "Digital Transformation", "Product Development", "Strategic Roadmapping"],
  //   bio: [
  //     "I have worked with companies of various sizes to develop cutting-edge technology products and solutions. I am dedicated to digital transformation and shaping the technological landscape, ensuring the establishment of the right strategic technology roadmap and seamless execution.",
  //     "My goal is to make technology easily accessible and to enhance the quality of life for all users. At FOWND, I focus on building scalable technology solutions that can transform how healthcare providers and patients interact.",
  //   ],
  // },
  piyush: {
    name: "Piyush Patil",
    location: "Chicago, IL",
    image: "/assets/images/teams/Piyush.png",
    specialties: ["Full-Stack Engineering", "Microservices", "Real-time Systems", "Software Architecture"],
    bio: [
      "My journey began at top institutions in India and the U.S., where I built a strong foundation in computer engineering and discovered my passion for tech innovation. I've engineered interactive platforms at USC, tackled data-driven medical projects, and built scalable tech solutions—constantly embracing new challenges with innovation and impact.",
      "Today, as a founding engineer at Fownd, I mix my diverse experiences to craft modern, efficient solutions that truly push the envelope. I love working with cool technologies like microservices and real-time streaming, keeping things simple yet powerful.",
    ],
  },
  rohan: {
    name: "Rohan Nalla",
    location: "Chicago, IL",
    image: "/assets/images/teams/Rohan.png",
    specialties: ["Data Science", "AI Automation", "Machine Learning", "Healthcare Analytics"],
    bio: [
      "With a background in Computer Science and Engineering, I pivoted to mastering Data Science at CU Boulder, after recognizing its potential to unify diverse fields. I explored how data enhances decision-making across varied domains, shaping a broad perspective that helps me bridge gaps between siloed areas.",
      "At FOWND, I apply this mindset with AI agents to reshape automation. I build solutions that streamline processes and bring agility to legacy healthcare systems. My goal is to leverage AI to transform workflows, ensuring technology doesn't just assist but redefines how we operate.",
    ],
  },
  yashaswi: {
    name: "Yashaswi Galhotra",
    location: "Chicago, IL",
    image: "/assets/images/teams/Yashaswi.png",
    specialties: ["Machine Learning", "AI-driven Healthcare", "Predictive Modeling", "Real-time Monitoring"],
    bio: [
      "A graduate of the University of Texas and BITS Pilani, Yashaswi is a committed Machine Learning Engineer who leverages AI to transform healthcare. His expertise includes real-time patient monitoring, predictive disease modeling, and the development of AI-driven therapeutic tools, all focused on significantly improving patient care and outcomes.",
    ],
  },
}

const TeamDetail: React.FC = () => {
  const navigate = useNavigate()
  const { name } = useParams<{ name: string }>()
  const member = teamDetails[name as keyof typeof teamDetails]

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  if (!member) {
    return <div>Member not found</div>
  }

  const handleBack = () => {
    navigate("/about")
  }

  const handleSendMessage = () => {
    window.open("https://bookings.cloud.microsoft/book/NotationAI@fownd.org/?ismsaljsauthenabled", "_blank")
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">

      {/* Hero Section */}
      <section className="relative py-20 px-6 bg-gradient-to-br from-[#1e3d3b] via-[#2a5a56] to-[#1e3d3b]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-[#1e3d3b]/20 to-[#1e3d3b]/40"></div>

        <div className="relative mx-auto max-w-6xl">
          <div className="mb-16 mt-8">
            <button
              onClick={handleBack}
              className="group flex items-center text-white/80 hover:text-white transition-all duration-300 font-medium cursor-pointer"
            >
              <svg
                className="w-5 h-5 mr-3 group-hover:-translate-x-1 transition-transform duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Team
            </button>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-wide font-productSans">
                {member.name}
              </h1>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start text-[#619dd2] mb-8">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-[#619dd2]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-lg text-[#619dd2] font-bold font-inter">{member.location}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="relative">
                <div className="w-80 h-80 rounded-2xl overflow-hidden ring-8 ring-white/20 shadow-2xl">
                  <img
                    src={member.image || "/placeholder.svg"}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-white/20 rounded-full blur-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 px-6">
        <div className="mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg">
                <div className="p-8">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6">About {member.name.split(" ")[0]}</h2>
                  <div className="space-y-6">
                    {member.bio.map((paragraph, index) => (
                      <p key={index} className="text-slate-600 leading-relaxed text-lg">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg">
                <div className="p-6">
                  <h3 className="text-lg font-bold text-slate-800 mb-4">Specialties</h3>
                  <div className="flex flex-wrap gap-2">
                    {member.specialties.map((specialty, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 rounded-full text-sm bg-emerald-100 text-[#619DD2]"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#1e3d3b] to-[#2a5a56] text-white rounded-lg shadow-lg">
                <div className="p-6">
                  <h3 className="text-lg font-bold mb-4">Get in Touch</h3>
                  <p className="text-emerald-100 mb-4 text-sm">
                    Interested in learning more about our mission or discussing potential collaborations?
                  </p>
                  <button
                    onClick={handleSendMessage}
                    className="w-full flex items-center justify-center px-4 py-2 bg-white text-[#619DD2] hover:bg-[#619DD2]/10 rounded-md transition-colors"
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Send Message
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default TeamDetail
